import { useCallback, useEffect, useRef, useState } from 'react';
import { claimAudioFocus, releaseAudioFocus, stopAllAudio } from '../utils/audioFocus';
import { narrateText } from '../services/gnNarrationService';

export type VoiceMode = 'push-to-talk' | 'wake-word';

const WAKE_WORD = 'guruji';

/** Minimal shape of the Web Speech API — not in standard TS lib.dom typings. */
interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
}

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function isVoiceSupported(): boolean {
  return getSpeechRecognitionCtor() !== null;
}

/**
 * Voice in/out for Guruji NoteBook (spec 046 US5). No audio is captured, and no microphone
 * permission requested, until the user explicitly enables voice (FR-019). STT runs entirely
 * in the browser (Web Speech API) — only the final transcript TEXT ever leaves the device,
 * so raw audio is never uploaded (FR-037 satisfied by architecture, not a server-side
 * discard step). Two modes: push-to-talk (default, capture only while held) and an opt-in
 * ambient wake-word mode listening for "Guruji".
 */
export function useVoice(onTranscript: (text: string) => void) {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<VoiceMode>('push-to-talk');
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [supported] = useState(isVoiceSupported());

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopSpeaking = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    setSpeaking(false);
  }, []);

  const enable = useCallback(() => setEnabled(true), []);
  const disable = useCallback(() => {
    setEnabled(false);
    recognitionRef.current?.stop();
    stopSpeaking();
  }, [stopSpeaking]);

  const buildRecognition = useCallback((continuous: boolean) => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return null;
    const recognition = new Ctor();
    recognition.continuous = continuous;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (event: any) => {
      const transcript: string = event.results[event.results.length - 1][0].transcript.trim();
      if (mode === 'wake-word') {
        const lower = transcript.toLowerCase();
        if (!lower.startsWith(WAKE_WORD)) return; // background speech — never acted on (edge case)
        const question = transcript.slice(transcript.toLowerCase().indexOf(WAKE_WORD) + WAKE_WORD.length).replace(/^[,:\s]+/, '');
        if (question) onTranscript(question);
      } else {
        if (transcript) onTranscript(transcript);
      }
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => {
      if (mode === 'wake-word' && enabled) {
        // Ambient mode keeps listening for the rest of the session.
        try { recognition.start(); } catch { /* already running */ }
      } else {
        setListening(false);
      }
    };
    return recognition;
  }, [mode, enabled, onTranscript]);

  // Push-to-talk: hold to capture.
  const startTalking = useCallback(() => {
    if (!enabled || !supported || mode !== 'push-to-talk') return;
    stopAllAudio(); // a new voice interaction stops any prior narration (edge case)
    const recognition = buildRecognition(false);
    if (!recognition) return;
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  }, [enabled, supported, mode, buildRecognition]);

  const stopTalking = useCallback(() => {
    if (mode !== 'push-to-talk') return;
    recognitionRef.current?.stop();
    setListening(false);
  }, [mode]);

  // Wake-word: starts/stops with enabled + mode.
  useEffect(() => {
    if (!enabled || !supported || mode !== 'wake-word') {
      if (recognitionRef.current && mode !== 'push-to-talk') {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      return;
    }
    const recognition = buildRecognition(true);
    if (!recognition) return;
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
    return () => {
      recognition.onend = null; // prevent the auto-restart-on-end handler during teardown
      recognition.stop();
      setListening(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, supported, mode]);

  const speak = useCallback(async (text: string) => {
    if (!enabled) return;
    try {
      const audioDataUri = await narrateText(text);
      claimAudioFocus(stopSpeaking);
      const audio = new Audio(audioDataUri);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => { setSpeaking(false); releaseAudioFocus(stopSpeaking); };
      await audio.play().catch(() => setSpeaking(false));
    } catch {
      // Narration failure degrades to silent (on-screen text is always shown separately).
    }
  }, [enabled, stopSpeaking]);

  return {
    supported, enabled, mode, listening, speaking,
    enable, disable, setMode,
    startTalking, stopTalking,
    speak, stopSpeaking,
  };
}
